import { Clear, MoreHoriz } from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Badge, CircularProgress } from "@mui/material";
import { Col, Row } from "antd";
import { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast } from "react-toastify";

import styled from "@emotion/styled";
import { CommentOutlined } from "@mui/icons-material";
import { Avatar, HorizontalLine, MultiLineText } from "Components";
import OpentSideBar from "Components/OpentSideBar";
import { useAppContext } from "Context/AppContext";
import { ResponseDataType, getRequest } from "api";
import {
  EndStreamFollowerStatus,
  ProposalType,
  StreamType,
  TaskType,
} from "constant/enum";
import moment from "moment";
import styles from "../stream.module.css";
import {
  AttachedFile,
  ChatInStream,
  CommentBox,
  MemberFullInfo,
  MemberInfo,
  MemberInfoDataType,
  StreamAction,
  StreamInfoDataType,
  StreamItemInTimeline,
} from "./Components";
import {
  AddNewDynamicStream,
  AddNewFollower,
} from "./Components/StreamAction/ActionButton";
import { StreamDetailProvider } from "./Context/StreamDetailContext";
import { motion, AnimatePresence } from "framer-motion";

interface StreamDetailDataType {
  creator: MemberInfoDataType;
  assignee: MemberInfoDataType;
  description: string;
  createAt: string;
  streamList: StreamInfoDataType[];
  isEnd: number;
  numberOfNewMsg: number;
}

export const StreamDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentOrg, onEvent } = useAppContext();
  const { streamId } = useParams();
  const [streamDetail, setStreamDetail] = useState<StreamDetailDataType>();
  const [selectedStream, setSelectedStream] = useState<StreamInfoDataType>();
  const [showChat, setShowChat] = useState<boolean>(false);
  const [numberOfNewMsg, setNumberOfNewMsg] = useState<number>(0);
  const [isDynamicStream, setIsDynamicStream] = useState<boolean>(false);
  const [myInfo, setMyInfo] = useState<MemberInfoDataType | null>(null);
  const [shouldShowDetail, setShouldShowDetail] = useState(false);
  const [isCreatingNewStream, setIsCreatingNewStream] = useState(false);
  const [isCreatingNewFollower, setIsCreatingNewFollower] = useState(false);

  const GetStreamDetail = async (isFirstTime: boolean) => {
    const response: ResponseDataType<StreamDetailDataType> = await getRequest(
      `/stream/${currentOrg}/stream-detail/${streamId}`
    );
    console.log(response);

    if (response.code) {
      toast.error(response.message);
      navigate(
        location.pathname.includes("proposal-detail")
          ? "/proposal-list"
          : "/request-list"
      );
    } else {
      setStreamDetail(response.data);
      setNumberOfNewMsg(response.data?.numberOfNewMsg || 0);
      const theFirstStream = response.data?.streamList[0];
      setIsDynamicStream(
        (theFirstStream?.type === StreamType.PROPOSAL &&
          theFirstStream?.missionType === ProposalType.DYNAMIC) ||
          (theFirstStream?.type === StreamType.TASK &&
            theFirstStream.missionType === TaskType.DYNAMIC)
      );
      if (isFirstTime) {
        const notiType = searchParams.get("type");
        const relateId = searchParams.get("relateId");
        if (
          relateId &&
          notiType &&
          ["2", "3", "4", "5", "7"].includes(notiType)
        ) {
          const matched = response.data?.streamList.find(stream => stream.id === relateId);
          setSelectedStream(matched ?? undefined);
          searchParams.delete("type");
          searchParams.delete("relateId");
          setSearchParams(searchParams);
        } else setSelectedStream(undefined);

        if (searchParams.get("showChat") === "true") {
          setNumberOfNewMsg(0);
          setShowChat(true);
        }
      } else {
        setSelectedStream(prev =>
          response.data?.streamList.find(stream => stream.id === prev?.id)
        );
      }
    }
  };

  useEffect(() => {
    GetStreamDetail(true);
    setShowChat(false);
  }, [streamId, navigate]);

  useEffect(() => {
    onEvent("update-stream", updateStreamId => {
      if (updateStreamId === streamId) {
        // setIsCreatingNewStream(false);
        GetStreamDetail(false);
      }
    });
    // eslint-disable-next-line
  }, [onEvent, streamId, navigate]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (selectedStream && !showChat) {
      timer = setTimeout(() => setShouldShowDetail(true), 100);
    } else {
      setShouldShowDetail(false); 
    }

    return () => clearTimeout(timer);
  }, [selectedStream, showChat]);

  useEffect(() => {
    if (!showChat) {
      onEvent("new-stream-chat", (res: { rootId: string }) => {
        const newChatStreamRootId = res.rootId;
        if (streamId === newChatStreamRootId && !showChat) {
          setNumberOfNewMsg(prev => prev + 1);
        }
      });
    }
  }, [onEvent, streamId, setNumberOfNewMsg, showChat]);

  const toggleChat = () => {
    if (!showChat) setNumberOfNewMsg(0);
    setShowChat(prev => !prev);
  };

  console.log("selectedStream: ", selectedStream);

  return (
    <div className={styles.stream_detail_wrapper}>
      {streamDetail ? (
        <Row className={styles.stream_detail} 
        justify={"space-between"}
        >
          <Col span={10}>
            <motion.div
              animate={{
                x: !showChat && !selectedStream && !isCreatingNewStream && !isCreatingNewFollower ? "70%" : "0%",
              }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              style={{ width: "100%" }}
            >
              <div className={styles.stickyHeader}>
              <Row justify="space-between" align="middle">
                <Col span={22}>
                  <Row>
                    <Avatar
                      src={streamDetail?.creator?.avatar}
                      className={styles.creator_avatar}
                    />
                    <div>
                      <div className={styles.creator_name}>
                        {streamDetail?.creator && <MemberInfo {...streamDetail?.creator} />}
                      </div>
                      <div style={{ fontSize: 12 }}>
                        <span style={{ fontWeight: 700 }}>Ngày tạo: </span>
                        <span>
                          {moment(streamDetail.createAt).format(
                            "HH:mm DD/MM/YYYY"
                          )}
                        </span>
                      </div>
                    </div>
                  </Row>
                </Col>
                <Col span={2}>
                  <OpenChatWrapper onClick={toggleChat}>
                    <NewChatNotiNumber
                      badgeContent={numberOfNewMsg}
                      color="error"
                    >
                      <CommentOutlined sx={{ color: "white", fontSize: 25 }} />
                    </NewChatNotiNumber>
                  </OpenChatWrapper>
                </Col>
              </Row>
              <HorizontalLine style={{ margin: "15px 0" }} />
              <Row justify="space-between" align="middle">
                <div style={{ maxWidth: "calc(100% - 40px)" }}>
                  <div className={styles.content_label}>Nội dung </div>
                  <div style={{ fontWeight: 700, height: "max-content" }}>
                    {streamDetail.description}
                  </div>
                </div>
                <AddNewFollower 
                  onStartCreate={() => setIsCreatingNewFollower(true)}
                  onCancelCreate={() => setIsCreatingNewFollower(false)}
                />
              </Row>
              <HorizontalLine style={{ margin: "15px 0" }} />
              </div>
              <div className={styles.content_label}>Tiến trình xử lý</div>
              {streamDetail.streamList.map((streamItem, index) => (
                <StreamItemInTimeline
                  key={index}
                  isSelected={streamItem.id === selectedStream?.id}
                  isTheLast={
                    !(
                      isDynamicStream &&
                      streamDetail.isEnd === EndStreamFollowerStatus.IN_PROGRESS
                    ) && index === streamDetail.streamList.length - 1
                  }
                  select={() => {
                    if (selectedStream?.id === streamItem.id) {
                      setSelectedStream(undefined); 
                    } else {
                      setSelectedStream(streamItem); 
                    }
                    setShowChat(false);
                  }}
                  {...streamItem}
                />
              ))}
              {isDynamicStream &&
                streamDetail.isEnd === EndStreamFollowerStatus.IN_PROGRESS && (
                  <AddNewDynamicStream 
                    onStartCreate={() => setIsCreatingNewStream(true)}
                    onCancelCreate={() => setIsCreatingNewStream(false)} 
                  />
                )
              }
            </motion.div>
          </Col>
          {showChat && <ChatInStream rootId={streamId || ""} />}
          {shouldShowDetail && (
            <Col span={12}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 30 }}
                transition={{ duration: 0.4 }}
                className={styles.stream_detail_scrollable}
              >
              <div className={styles.content_label}>Thông tin chi tiết</div>
              <div className={styles.selected_stream_label}>Người thực hiện</div>
              <MemberFullInfo
                {...(selectedStream?.assignee as MemberInfoDataType)}
              />
              <HorizontalLine style={{ margin: "15px 0" }} />
              <div className={styles.selected_stream_label}>
                Nội dung{" "}
                {selectedStream?.optionValue === 1 && (
                  <CheckCircleIcon
                    style={{
                      fill: "#07bc0c",
                      fontSize: 15,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "3px 6px 0px",
                    }}
                  />
                )}

                {selectedStream?.optionValue === 0 && (
                  <Clear
                    style={{
                      backgroundColor: "#D04339",
                      fill: "white",
                      fontSize: 15,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "3px 6px 0px",
                      borderRadius: "50%"
                    }}
                  />
                )}

                {selectedStream?.optionValue === null && selectedStream?.canUpdate === false && (
                  <MoreHoriz
                    style={{
                      backgroundColor: "#CEF1C0",
                      fill: "white",
                      fontSize: 15,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      margin: "3px 6px 0px",
                      borderRadius: "50%"
                    }}
                  />
                )}

              </div>
              <MultiLineText
                style={{ fontWeight: 700 }}
                text={
                  selectedStream?.content || selectedStream?.description || ""
                }
              />
              <HorizontalLine style={{ margin: "15px 0" }} />
              {selectedStream && (
                <StreamDetailProvider
                  isStreamFollowEnd={streamDetail.isEnd}
                  selectedStreamId={selectedStream.id}
                >
                  <StreamAction
                    streamId={selectedStream.id}
                    {...selectedStream}
                  />
                  <AttachedFile isAssignee={selectedStream.isAssignee} />
                  <CommentBox setMyInfo={setMyInfo} />
                </StreamDetailProvider>
              )}
              </motion.div>
            </Col>
          )}
        </Row>
      ) : (
        <CircularProgress size={50} sx={{ color: "#141d49", marginTop: 10 }} />
      )}
      <OpentSideBar />
    </div>
  );
};

const OpenChatWrapper = styled.div`
  background-color: rgb(0, 0, 0);
  padding: 12px 11px 8px 11px;
  border-radius: 50%;
  width: max-content;
  cursor: pointer;
`;

const NewChatNotiNumber = styled(Badge)`
  .MuiBadge-badge {
    top: -9px;
    right: -9px;
  }
`;
